import {Card, CardContent, Typography, Stack, Box, Paper, useTheme, Container, CircularProgress} from "@mui/material";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as ReTooltip, ResponsiveContainer, Cell,
} from "recharts";
import ComponentLoader from "./ComponentLoader";
import {ShareListDTO} from "../../types/DTOs/ShareListDTO";
import React, {useEffect, useState} from "react";
import {DashboardAPI} from "../../services/API/DashboardAPI";
import {Log} from "../../utils/Log";
import {ShareListDashboardDTO} from "../../types/DTOs/Dashboard/ShareListDashboardDTO";

const isExpired = (date: Date): boolean => new Date(date) < new Date();
const truncateToken = (token: string): string => token.slice(0, 12) + "…";

interface TooltipEntry { value?: number | string }
function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipEntry[]; label?: string }) {
    if (!active || !payload?.length) return null;
    return (
        <Paper elevation={4} sx={{ p: 1.5, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={700} color="primary">
                {payload[0].value} visualizzazioni
            </Typography>
        </Paper>
    );
}

function buildBarData(shares: ShareListDTO[]) {
    if (shares === null || shares === undefined) {
        return [];
    }

    if (shares.length === 0) {
        return [];
    }

    return shares
        .filter((s) => !s.isDeleted)
        .map((s) => ({
            name: truncateToken(s.shareToken),
            views: s.viewCount,
            active: !isExpired(s.expirationDate),
        }))
        .sort((a, b) => b.views - a.views);
}

export default function ViewsBarChart() {
    const theme = useTheme();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<ShareListDashboardDTO[]>([]);
    const [barData, setBarData] = useState<{name: string, views: number, active: boolean}[]>([]);

    useEffect(() => {
        loadData();
    }, [])

    useEffect(() => {
        console.log(data);
        setBarData(buildBarData(data));
    }, [data]);

    const loadData = async () =>{
        setLoading(true);
        setError('');
        // caricamento dati
        const dataReturn = await DashboardAPI.Instance<DashboardAPI>().getSharesList();
        setData(dataReturn);

        setLoading(false);
    };

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <ComponentLoader loading={loading} error={error} skeletonHeight={280}>
            <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%" }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                        Visualizzazioni per Link
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                        Confronto tra i link di condivisione
                    </Typography>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={barData} barSize={28}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
                            <ReTooltip content={<CustomTooltip />} cursor={{ fill: theme.palette.action.hover }} />
                            <Bar dataKey="views" radius={[6, 6, 0, 0]}>
                                {barData.map((entry, i) => (
                                    <Cell key={i} fill={entry.active ? "#6C63FF" : theme.palette.action.disabled} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <Stack direction="row" spacing={2} mt={1}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Box sx={{ width: 10, height: 10, bgcolor: "#6C63FF", borderRadius: 1 }} />
                            <Typography variant="caption" color="text.secondary">Attivo</Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Box sx={{ width: 10, height: 10, bgcolor: "action.disabled", borderRadius: 1 }} />
                            <Typography variant="caption" color="text.secondary">Scaduto</Typography>
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </ComponentLoader>
    );
}